import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Edit, ExternalLink } from 'lucide-react';
import { StatusBadge } from './StatusBadge';
import { useLanguage } from '../../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function TransferViewDialog({ open, onOpenChange, transfer, onEdit }) {
  const { t } = useLanguage();
  if (!transfer) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{t('admin_transfers_detail')}</DialogTitle></DialogHeader>
        <div className="space-y-6 mt-4 max-h-[70vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-muted-foreground mb-1">{t('send_transfer_id')}</div>
              <div className="font-mono text-xs">{transfer.id}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">{t('admin_transfers_sender')}</div>
              <div className="font-medium text-primary">{transfer.sender_name || '-'}</div>
              <div className="text-xs text-muted-foreground">{transfer.sender_email || ''}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-muted-foreground mb-1">{t('admin_transfers_amount_jpy')}</div>
              <div className="text-2xl font-bold font-mono text-primary">¥{transfer.amount_jpy.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">{t('admin_transfers_amount_brl')}</div>
              <div className="text-2xl font-bold font-mono text-accent">R$ {transfer.amount_brl.toLocaleString()}</div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-muted-foreground mb-1">{t('fee')}</div>
              <div className="font-mono">¥{transfer.fee_jpy.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">{t('total')}</div>
              <div className="font-mono">¥{transfer.total_jpy.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">{t('send_exchange_rate')}</div>
              <div className="font-mono">{transfer.exchange_rate.toFixed(5)}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-muted-foreground mb-1">{t('admin_users_status')}</div>
              <StatusBadge status={transfer.status} type="transfer" />
            </div>
            <div>
              <div className="text-sm text-muted-foreground mb-1">{t('admin_transfers_payment_status')}</div>
              <div className="font-medium">
                {transfer.payment_status === 'awaiting' && t('status_pending')}
                {transfer.payment_status === 'paid' && t('status_completed')}
                {transfer.payment_status === 'confirmed' && t('admin_deposits_status_confirmed')}
              </div>
            </div>
          </div>

          {transfer.status === 'completed' && (
            <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-xl p-4" data-testid="view-completion-details">
              <div className="text-sm font-semibold text-emerald-700 mb-3">{t('admin_transfers_completion_info')}</div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-muted-foreground mb-0.5">{t('admin_transfers_settlement_id')}</div>
                  <div className="font-mono font-medium" data-testid="view-settlement-id">{transfer.settlement_id || <span className="text-muted-foreground/50">{t('not_entered')}</span>}</div>
                </div>
                <div>
                  <div className="text-muted-foreground mb-0.5">{t('admin_transfers_completed_date')}</div>
                  <div data-testid="view-completed-at">{transfer.completed_at ? new Date(transfer.completed_at).toLocaleDateString('ja-JP') : <span className="text-muted-foreground/50">{t('not_entered')}</span>}</div>
                </div>
                <div>
                  <div className="text-muted-foreground mb-0.5">{t('admin_transfers_settled_amount')}</div>
                  <div className="font-mono font-bold text-accent" data-testid="view-settled-amount">{transfer.settled_amount ? `R$ ${transfer.settled_amount.toLocaleString()}` : <span className="text-muted-foreground/50 font-normal">{t('not_entered')}</span>}</div>
                </div>
              </div>
              <div className="mt-3">
                <div className="text-muted-foreground mb-1 text-sm">{t('admin_transfers_proof')}</div>
                {transfer.proof_image ? (
                  <div>
                    <div className="border border-border rounded-lg overflow-hidden bg-white" data-testid="view-proof-image">
                      <iframe src={`${API_URL}${transfer.proof_image}`} className="w-full" style={{ height: '280px' }} title={t('admin_transfers_proof_pdf')} />
                    </div>
                    <a href={`${API_URL}${transfer.proof_image}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-2 text-xs text-blue-600 hover:text-blue-800">
                      <ExternalLink className="h-3 w-3" />{t('admin_transfers_open_new_tab')}
                    </a>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground/50 p-2.5 border border-dashed border-border rounded-lg text-center">{t('admin_transfers_not_uploaded')}</div>
                )}
              </div>
            </div>
          )}

          {/* Recipient Info */}
          <div className="border border-border/50 rounded-xl p-4 bg-secondary/20" data-testid="detail-recipient-section">
            <h4 className="font-heading font-semibold text-primary mb-3">{t('admin_transfers_recipient')}</h4>
            {transfer.recipient ? (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-muted-foreground mb-0.5">{t('name')}</div>
                  <div className="font-medium">{transfer.recipient.full_name}</div>
                </div>
                <div>
                  <div className="text-muted-foreground mb-0.5">{t('recipients_cpf')}</div>
                  <div className="font-mono text-xs">{transfer.recipient.document_number}</div>
                </div>
                <div>
                  <div className="text-muted-foreground mb-0.5">{t('recipients_bank')}</div>
                  <div>{transfer.recipient.bank_name}</div>
                </div>
                <div>
                  <div className="text-muted-foreground mb-0.5">{t('recipients_type')}</div>
                  <div>{transfer.recipient.account_type === 'pix' ? 'PIX' : t('recipients_bank_account')}</div>
                </div>
                {transfer.recipient.pix_key && (
                  <div className="col-span-2">
                    <div className="text-muted-foreground mb-0.5">{t('recipients_pix')}</div>
                    <div className="font-mono text-xs">{transfer.recipient.pix_key}</div>
                  </div>
                )}
                {transfer.recipient.account_number && (
                  <div>
                    <div className="text-muted-foreground mb-0.5">{t('recipients_account')}</div>
                    <div className="font-mono text-xs">{transfer.recipient.account_number}</div>
                  </div>
                )}
                {transfer.recipient.branch_number && (
                  <div>
                    <div className="text-muted-foreground mb-0.5">{t('recipients_branch_number')}</div>
                    <div className="font-mono text-xs">{transfer.recipient.branch_number}</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">{t('admin_data_not_found')}</div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-muted-foreground mb-1">{t('history_date')}</div>
              <div className="text-sm">{new Date(transfer.created_at).toLocaleString('ja-JP')}</div>
            </div>
            {transfer.completed_at && (
              <div>
                <div className="text-sm text-muted-foreground mb-1">{t('admin_transfers_completed_at')}</div>
                <div className="text-sm">{new Date(transfer.completed_at).toLocaleString('ja-JP')}</div>
              </div>
            )}
          </div>

          <Button variant="outline" className="w-full" onClick={onEdit} data-testid="view-to-edit-button">
            <Edit className="h-4 w-4 mr-2" /> {t('admin_transfers_edit_status_btn')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
